import { useMemo, useState } from "react";
import { useMyLedgerQuery } from "../../../store/service/clientBetService";
import "./Client.scss";

const typeOptions = [
  { id: "all", label: "All" },
  { id: "deposit", label: "Deposit/Withdraw" },
  { id: "bet", label: "Betting" },
];

const ClientAccountStatement = () => {
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [type, setType] = useState("deposit");
  const [pageSize, setPageSize] = useState(25);
  const [applied, setApplied] = useState({ search: "", from, to, type });

  const { data, isLoading, isFetching } = useMyLedgerQuery();

  const rows = useMemo(() => {
    let list = [...(data?.data || [])];
    const fromTs = applied.from ? new Date(applied.from).setHours(0, 0, 0, 0) : 0;
    const toTs = applied.to ? new Date(applied.to).setHours(23, 59, 59, 999) : Date.now();

    list = list.filter((row) => {
      const ts = new Date(row.created_at || row.createdAt || row.date).getTime();
      if (Number.isFinite(ts) && (ts < fromTs || ts > toTs)) return false;

      const pay = String(row.payment_type || row.paymentType || row.collection || "").toLowerCase();
      const remark = String(row.remark || "").toLowerCase();
      if (applied.type === "deposit") {
        if (!/deposit|withdraw|cash|credit|debit/.test(`${pay} ${remark}`)) {
          // still show ledger cash-like rows; if empty types keep all
          if (pay && !/dep|wid|cash|credit|debit/.test(pay)) return false;
        }
      } else if (applied.type === "bet") {
        if (!/bet|settle|fancy|bookmaker|odds/.test(`${pay} ${remark}`)) return false;
      }

      if (applied.search.trim()) {
        const q = applied.search.toLowerCase();
        const blob = `${row.remark || ""} ${row.collection || ""} ${row.payment_type || ""} ${row.amount || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });

    return list.slice(0, pageSize);
  }, [data, applied, pageSize]);

  const onSubmit = (e) => {
    e.preventDefault();
    setApplied({ search, from, to, type });
  };

  return (
    <div className="w777-page">
      <h2 className="w777-page-title">Account Statement</h2>

      <form className="w777-filter-card" onSubmit={onSubmit}>
        <div className="w777-search-row">
          <input
            type="search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="w777-search-ico" aria-hidden="true">
            ⌕
          </span>
        </div>

        <div className="w777-date-row">
          <label>
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>

        <label className="w777-type-row">
          Type
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {typeOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <button type="submit" className="w777-submit-btn">
          Submit
        </button>
      </form>

      <div className="w777-table-tools">
        <label>
          Show{" "}
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>{" "}
          Entries
        </label>
        <div className="w777-export-icos" aria-hidden="true">
          <span title="PDF">PDF</span>
          <span title="Excel">XLS</span>
        </div>
      </div>

      <div className="w777-data-table-wrap">
        <table className="w777-data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sr no</th>
              <th>Credit</th>
              <th>Debit</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="w777-empty-cell">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="w777-empty-cell">
                  no records found
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const amt = Number(row.amount) || 0;
                const credit = amt > 0 ? amt : 0;
                const debit = amt < 0 ? Math.abs(amt) : 0;
                const date = row.created_at || row.createdAt || "—";
                return (
                  <tr key={row.id || i}>
                    <td>{String(date).slice(0, 16).replace("T", " ")}</td>
                    <td>{i + 1}</td>
                    <td>{credit ? credit.toFixed(2) : "—"}</td>
                    <td>{debit ? debit.toFixed(2) : "—"}</td>
                    <td>{row.remark || row.collection || row.payment_type || "—"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {isFetching ? <div className="w777-refresh">Refreshing…</div> : null}
    </div>
  );
};

export default ClientAccountStatement;
